import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Button from './ui/Button';
import { ArrowRight, ArrowUpRight, MailIcon } from './ui/Icons';
import { CALENDLY_URL, CONTACT_EMAIL } from '../data/site';

const CALENDLY_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';

interface EventType {
  uri: string;
  name: string;
  description_plain?: string | null;
  description?: string | null;
  duration: number;
  scheduling_url: string;
  active: boolean;
}

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (options: { url: string; parentElement: HTMLElement }) => void;
    };
  }
}

let calendlyScriptPromise: Promise<void> | null = null;

const loadCalendlyScript = (): Promise<void> => {
  if (window.Calendly) return Promise.resolve();
  if (calendlyScriptPromise) return calendlyScriptPromise;

  calendlyScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = CALENDLY_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      calendlyScriptPromise = null;
      reject(new Error('Calendly script failed to load'));
    };
    document.body.appendChild(script);
  });

  return calendlyScriptPromise;
};

const Intro = styled.p`
  color: var(--text-2);
  margin-bottom: 1.5rem;
  max-width: 60ch;
`;

const EventList = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
`;

const EventCard = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.5rem;
  border-radius: var(--radius-md);
  background: var(--surface);
  border: 1px solid var(--border);
`;

const EventName = styled.h3`
  font-size: 1.15rem;
  font-weight: 600;
`;

const Duration = styled.p`
  font-size: var(--fs-small);
  font-weight: 600;
  color: var(--accent);
`;

const EventDescription = styled.p`
  color: var(--text-2);
  font-size: 0.95rem;
  flex-grow: 1;
`;

const CardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Notice = styled.div<{ $tone?: 'error' | 'neutral' }>`
  padding: 1.25rem 1.5rem;
  border-radius: var(--radius-md);
  border: 1px solid ${({ $tone }) => ($tone === 'error' ? 'rgba(255, 123, 123, 0.4)' : 'var(--border)')};
  background: ${({ $tone }) => ($tone === 'error' ? 'rgba(255, 123, 123, 0.08)' : 'var(--surface)')};
  margin-bottom: 1.25rem;

  h3 {
    font-size: 1.05rem;
    margin-bottom: 0.4rem;
  }

  p {
    color: var(--text-2);
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const EmailLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--accent);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
`;

const EmbedToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const EmbedBox = styled.div`
  width: 100%;
  min-height: 660px;
  height: 660px;
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--bg-2);
  border: 1px solid var(--border);

  .calendly-inline-widget,
  iframe {
    width: 100%;
    height: 100%;
  }

  @media (max-width: 560px) {
    height: 72vh;
    min-height: 520px;
  }
`;

const Skeleton = styled.div`
  height: 180px;
  border-radius: var(--radius-md);
  background: var(--surface);
  border: 1px solid var(--border);
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
`;

const withEmbedParams = (url: string) => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}hide_gdpr_banner=1`;
};

const InlineEmbed: React.FC<{ url: string }> = ({ url }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    loadCalendlyScript()
      .then(() => {
        const container = containerRef.current;
        if (cancelled || !container || !window.Calendly) return;
        container.innerHTML = '';
        window.Calendly.initInlineWidget({ url: withEmbedParams(url), parentElement: container });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (failed) {
    return (
      <Notice $tone="error">
        <h3>The calendar didn't load.</h3>
        <p>
          Open it directly on{' '}
          <EmailLink href={url} target="_blank" rel="noopener noreferrer">
            Calendly <ArrowUpRight />
          </EmailLink>{' '}
          or email{' '}
          <EmailLink href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</EmailLink>.
        </p>
      </Notice>
    );
  }

  return <EmbedBox ref={containerRef} aria-label="Calendly scheduling calendar" />;
};

const CalendlyScheduler: React.FC = () => {
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);

  const fetchEventTypes = useCallback(async () => {
    setStatus('loading');
    try {
      const response = await fetch('/api/calendly/event-types');
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      const data = await response.json();
      if (!data.collection || !Array.isArray(data.collection)) {
        throw new Error('Unexpected response shape');
      }
      const active = (data.collection as EventType[])
        .filter((event) => event.active === true)
        .sort((a, b) => a.duration - b.duration);
      setEventTypes(active);
      setStatus('ready');
    } catch (err) {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    fetchEventTypes();
  }, [fetchEventTypes]);

  if (embedUrl) {
    return (
      <div>
        <EmbedToolbar>
          {eventTypes.length > 0 ? (
            <Button variant="ghost" onClick={() => setEmbedUrl(null)}>
              ← All meeting types
            </Button>
          ) : (
            <span />
          )}
          <Button variant="secondary" href={embedUrl}>
            Open in Calendly <ArrowUpRight />
          </Button>
        </EmbedToolbar>
        <InlineEmbed url={embedUrl} />
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div aria-busy="true" aria-live="polite">
        <Intro>Loading available meeting types…</Intro>
        <SkeletonGrid>
          <Skeleton />
          <Skeleton />
        </SkeletonGrid>
      </div>
    );
  }

  if (status === 'error' || eventTypes.length === 0) {
    return (
      <div>
        <Notice $tone={status === 'error' ? 'error' : 'neutral'} role="status">
          <h3>{status === 'error' ? "We couldn't load meeting types." : 'No meeting types are listed right now.'}</h3>
          <p>You can still pick a time on our Calendly page, or email us and we'll set up a call.</p>
        </Notice>
        <Actions>
          <Button onClick={() => setEmbedUrl(CALENDLY_URL)}>
            Open the calendar <ArrowRight />
          </Button>
          {status === 'error' && (
            <Button variant="secondary" onClick={fetchEventTypes}>
              Try again
            </Button>
          )}
          <Button variant="ghost" href={`mailto:${CONTACT_EMAIL}`}>
            <MailIcon /> {CONTACT_EMAIL}
          </Button>
        </Actions>
      </div>
    );
  }

  return (
    <div>
      <Intro>
        Pick a meeting type to see open times. We'll use the call to understand your goals and constraints, and
        you'll leave with clear next steps.
      </Intro>
      <EventList>
        {eventTypes.map((event) => {
          const description = event.description_plain ?? event.description;
          return (
            <EventCard key={event.uri}>
              <EventName>{event.name}</EventName>
              <Duration>{event.duration} minutes</Duration>
              {description && <EventDescription>{description}</EventDescription>}
              <CardActions>
                <Button onClick={() => setEmbedUrl(event.scheduling_url)}>
                  Choose a time <ArrowRight />
                </Button>
              </CardActions>
            </EventCard>
          );
        })}
      </EventList>
    </div>
  );
};

export default CalendlyScheduler;
